import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

/**
 * RouteContext provides the current app prefix so components never need to
 * sniff location.pathname to decide between "/docs" vs "/studio".
 *
 * Values:
 *   appPrefix — top-level app segment driving all internal navigation:
 *     "/docs", "/studio", or "/share/<token>" when the visitor came in
 *     through a share link. The share prefix is preserved so any deep
 *     page URL stays copyable as a working share link. Shares are
 *     always read-only docs surfaces; no `/docs` segment is added.
 */
interface RouteContextValue {
  appPrefix: string;
}

const Ctx = createContext<RouteContextValue>({ appPrefix: "/studio" });

export function RouteContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const appPrefix = useMemo(() => {
    const path = location.pathname;
    const shareMatch = path.match(/^\/share\/([^/]+)/);
    if (shareMatch) return `/share/${shareMatch[1]}`;
    if (path.startsWith("/docs")) return "/docs";
    return "/studio";
  }, [location.pathname]);

  const value = useMemo(() => ({ appPrefix }), [appPrefix]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRouteContext(): RouteContextValue {
  return useContext(Ctx);
}
